import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { requireAuth } from "@/middleware/authGuard";
import { supabase } from "@/lib/supabaseClient";
import { getTenantId } from "@/lib/getTenant";
import AdminLayout from "@/components/feature/AdminLayout";
import ContentTab from "./tabs/ContentTab";
import ServicesTab from "./tabs/ServicesTab";
import GalleryTab from "./tabs/GalleryTab";
import SEOTab from "./tabs/SEOTab";
import PreviewTab from "./tabs/PreviewTab";
import DomainTab from "./tabs/DomainTab";

type Tab = "content" | "services" | "gallery" | "seo" | "preview" | "domain";

const TABS: { id: Tab; label: string; icon: string }[] = [
  { id: "content",  label: "Content",       icon: "ri-edit-2-line" },
  { id: "services", label: "Services",      icon: "ri-stack-line" },
  { id: "gallery",  label: "Gallery",       icon: "ri-gallery-line" },
  { id: "seo",      label: "SEO",           icon: "ri-search-eye-line" },
  { id: "preview",  label: "Preview",       icon: "ri-eye-line" },
  { id: "domain",   label: "Domain",        icon: "ri-global-line" },
];

type TenantInfo = {
  id: string;
  slug: string | null;
  name: string;
  status: string;
};

export default function AdminWebsitePage() {
  const navigate = useNavigate();
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [tenantInfo, setTenantInfo] = useState<TenantInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<Tab>("content");
  const [isPublished, setIsPublished] = useState(true);
  const [copied, setCopied] = useState(false);

  const fetchTenant = useCallback(async (tid: string) => {
    const [tenantRes, contentRes] = await Promise.all([
      supabase.from("saas_tenants").select("id, slug, name, status").eq("id", tid).maybeSingle(),
      supabase.from("tenant_website_content").select("is_published").eq("tenant_id", tid).maybeSingle(),
    ]);
    if (tenantRes.data) setTenantInfo(tenantRes.data as TenantInfo);
    if (contentRes.data) setIsPublished(contentRes.data.is_published ?? true);
    setLoading(false);
  }, []);

  useEffect(() => {
    const init = async () => {
      const isAuth = await requireAuth();
      if (!isAuth) { navigate("/login"); return; }
      const tid = await getTenantId();
      setTenantId(tid);
      if (tid) fetchTenant(tid);
      else setLoading(false);
    };
    init();
  }, [navigate, fetchTenant]);

  const publicUrl = tenantInfo?.slug ? `/s/${tenantInfo.slug}` : null;
  const fullPublicUrl = publicUrl ? `${window.location.origin}${publicUrl}` : null;

  const handleCopy = () => {
    if (!fullPublicUrl) return;
    navigator.clipboard.writeText(fullPublicUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  return (
    <AdminLayout>
      {/* Light header */}
      <div className="bg-white border-b border-slate-200">
        <div className="px-4 md:px-8 pt-6 pb-0">
          {/* Title row */}
          <div className="flex items-start justify-between gap-4 mb-5">
            <div>
              <h1 className="text-slate-800 font-bold text-xl">Website Editor</h1>
              <p className="text-slate-500 text-sm mt-0.5">Manage your public studio page</p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {/* Published badge */}
              <span className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border ${isPublished ? "bg-emerald-50 border-emerald-200 text-emerald-600" : "bg-slate-100 border-slate-200 text-slate-400"}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${isPublished ? "bg-emerald-500" : "bg-slate-300"}`}></span>
                {isPublished ? "Published" : "Hidden"}
              </span>
              {/* URL copy */}
              {fullPublicUrl && (
                <button
                  onClick={handleCopy}
                  className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border transition-all cursor-pointer whitespace-nowrap ${copied ? "bg-emerald-50 border-emerald-200 text-emerald-600" : "bg-white border-slate-200 text-slate-500 hover:border-slate-300 hover:text-slate-700"}`}
                >
                  <i className={copied ? "ri-check-line" : "ri-file-copy-line"}></i>
                  {copied ? "Copied!" : "Copy URL"}
                </button>
              )}
              {/* View site */}
              {publicUrl && (
                <a
                  href={publicUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 text-sm font-semibold bg-[#C8A96E] hover:bg-[#b8965a] text-white px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-external-link-line"></i>
                  View Site
                </a>
              )}
            </div>
          </div>

          {/* URL bar */}
          {fullPublicUrl ? (
            <div className="flex items-center gap-2 mb-4 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 max-w-lg">
              <i className="ri-global-line text-slate-400 text-sm flex-shrink-0"></i>
              <span className="text-slate-500 text-xs font-mono truncate">{fullPublicUrl}</span>
            </div>
          ) : !loading && (
            <div className="flex items-center gap-2 mb-4 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 max-w-lg">
              <i className="ri-error-warning-line text-amber-500 text-sm flex-shrink-0"></i>
              <span className="text-amber-600 text-xs">No public URL yet — contact Super Admin to assign a slug.</span>
            </div>
          )}

          {/* Tabs */}
          <div className="flex items-center gap-0 overflow-x-auto">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-all cursor-pointer whitespace-nowrap border-b-2 -mb-px ${
                  activeTab === tab.id
                    ? "text-[#9a7840] border-[#C8A96E]"
                    : "text-slate-500 border-transparent hover:text-slate-700 hover:border-slate-300"
                }`}
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className={tab.icon}></i>
                </span>
                {tab.label}
                {tab.id === "domain" && (
                  <span className="text-[9px] bg-[#C8A96E]/15 text-[#9a7840] px-1.5 py-0.5 rounded-full font-semibold uppercase tracking-wide">Add-on</span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tab content — light background */}
      <div className="bg-slate-50 min-h-screen">
        <div className="px-4 md:px-8 py-6">
          {loading ? (
            <div className="space-y-4 max-w-2xl">
              {[1,2,3].map(i => <div key={i} className="h-32 bg-white rounded-xl border border-slate-200 animate-pulse" />)}
            </div>
          ) : (
            <>
              {activeTab === "content" && tenantId && (
                <div className="max-w-2xl">
                  <ContentTab tenantId={tenantId} onPublishChange={setIsPublished} />
                </div>
              )}
              {activeTab === "services" && tenantId && (
                <div className="max-w-3xl">
                  <ServicesTab tenantId={tenantId} />
                </div>
              )}
              {activeTab === "gallery" && tenantId && (
                <GalleryTab tenantId={tenantId} />
              )}
              {activeTab === "seo" && tenantId && (
                <SEOTab tenantId={tenantId} tenantName={tenantInfo?.name ?? "Your Studio"} publicUrl={publicUrl} />
              )}
              {activeTab === "preview" && (
                <PreviewTab publicUrl={publicUrl} tenantSlug={tenantInfo?.slug ?? null} isPublished={isPublished} />
              )}
              {activeTab === "domain" && tenantId && (
                <DomainTab tenantId={tenantId} tenantSlug={tenantInfo?.slug ?? null} />
              )}
            </>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
